(function () {
  'use strict';

  var rootSelector = '.bv-subscription-popup';
  var initializedAttribute = 'data-bv-subscription-popup-initialized';

  if (window.BvSubscriptionPopup && typeof window.BvSubscriptionPopup.initAll === 'function') {
    window.BvSubscriptionPopup.initAll();
    return;
  }

  // Analytics are non-critical: let the subscription UI paint before tag handlers run.
  var popupAnalyticsQueue = [];
  var popupAnalyticsFrame = null;
  var popupAnalyticsTimer = null;
  function flushPopupAnalytics() {
    if (popupAnalyticsFrame !== null) {
      window.cancelAnimationFrame(popupAnalyticsFrame);
      popupAnalyticsFrame = null;
    }
    if (popupAnalyticsTimer !== null) {
      window.clearTimeout(popupAnalyticsTimer);
      popupAnalyticsTimer = null;
    }
    var pending = popupAnalyticsQueue.splice(0);
    pending.forEach(function (entry) {
      if (typeof window.gtag !== 'function') return;
      try {
        if (entry.parameters) window.gtag('event', entry.name, entry.parameters);
        else window.gtag('event', entry.name);
      } catch (error) {
        // Analytics must not interrupt the subscription flow.
      }
    });
  }
  function queuePopupAnalytics(eventName, parameters) {
    popupAnalyticsQueue.push({ name: eventName, parameters: parameters });
    if (popupAnalyticsFrame !== null || popupAnalyticsTimer !== null) return;
    if (document.visibilityState === 'hidden' || typeof window.requestAnimationFrame !== 'function') {
      popupAnalyticsTimer = window.setTimeout(flushPopupAnalytics, 0);
      return;
    }
    popupAnalyticsFrame = window.requestAnimationFrame(function () {
      popupAnalyticsFrame = null;
      popupAnalyticsTimer = window.setTimeout(flushPopupAnalytics, 0);
    });
  }
  window.addEventListener('pagehide', flushPopupAnalytics);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flushPopupAnalytics();
  });

  var popupStylesPromise = null;
  var bodyLockState = null;

  function loadPopupStyles(root) {
    if (popupStylesPromise) return popupStylesPromise;
    var url = root.getAttribute('data-popup-style-url');
    if (!url) return Promise.reject(new Error('Subscription popup stylesheet URL is missing.'));

    popupStylesPromise = new Promise(function (resolve, reject) {
      var link = document.createElement('link');
      var timeout = null;
      var settled = false;
      link.rel = 'stylesheet';
      link.href = url;
      link.setAttribute('data-bv-subscription-popup-styles', '');
      function finish(error) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        link.removeEventListener('load', onLoad);
        link.removeEventListener('error', onError);
        if (error) {
          link.remove();
          reject(error);
        } else {
          resolve();
        }
      }
      function onLoad() { finish(); }
      function onError() { finish(new Error('Subscription popup stylesheet failed to load.')); }
      link.addEventListener('load', onLoad);
      link.addEventListener('error', onError);
      timeout = window.setTimeout(onError, 15000);
      document.head.appendChild(link);
    }).catch(function (error) {
      popupStylesPromise = null; // A later explicit open can retry a failed request.
      throw error;
    });
    return popupStylesPromise;
  }

  function loadPopupMedia(scope, options) {
    if (!scope) return;
    var settings = options || {};
    var loading = settings.loading || 'eager';
    var fetchPriority = settings.fetchPriority || '';
    var criticalFetchPriority = settings.criticalFetchPriority || fetchPriority;

    // Apply picture sources before img.src so mobile does not also fetch the desktop image.
    Array.prototype.forEach.call(scope.querySelectorAll('source[data-popup-srcset]'), function (source) {
      source.setAttribute('srcset', source.getAttribute('data-popup-srcset'));
      source.removeAttribute('data-popup-srcset');
    });
    Array.prototype.forEach.call(scope.querySelectorAll('img[data-popup-src], img[data-popup-srcset]'), function (image) {
      image.setAttribute('loading', loading);
      image.setAttribute('decoding', 'async');
      var imagePriority = image.hasAttribute('data-popup-critical-image') ? criticalFetchPriority : fetchPriority;
      if (imagePriority) {
        image.setAttribute('fetchpriority', imagePriority);
      }
      if (image.hasAttribute('data-popup-srcset')) {
        image.setAttribute('srcset', image.getAttribute('data-popup-srcset'));
        image.removeAttribute('data-popup-srcset');
      }
      if (image.hasAttribute('data-popup-src')) {
        image.setAttribute('src', image.getAttribute('data-popup-src'));
        image.removeAttribute('data-popup-src');
      }
    });
  }

  function waitForPopupImage(image, maxWait) {
    if (!image) return Promise.resolve();
    return new Promise(function (resolve) {
      var settled = false;
      // Never indefinitely block a subscription on an optional image or failed network.
      var timeout = window.setTimeout(finish, typeof maxWait === 'number' ? maxWait : 2500);
      function finish() {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        image.removeEventListener('load', onLoad);
        image.removeEventListener('error', finish);
        resolve();
      }
      function onLoad() {
        if (image.naturalWidth && typeof image.decode === 'function') {
          try { image.decode().then(finish, finish); } catch (error) { finish(); }
        } else {
          finish();
        }
      }
      image.addEventListener('load', onLoad);
      image.addEventListener('error', finish);
      if (image.complete) onLoad();
    });
  }

  function createSessionStore(prefix) {
    var memory = {};
    var storage = null;

    try {
      storage = window.sessionStorage;
      var testKey = prefix + '_storage_test';
      storage.setItem(testKey, '1');
      storage.removeItem(testKey);
    } catch (error) {
      storage = null;
    }

    return {
      get: function (key) {
        if (storage) {
          try {
            return storage.getItem(prefix + '_' + key);
          } catch (error) {
            return memory[key] || null;
          }
        }
        return memory[key] || null;
      },
      set: function (key, value) {
        if (storage) {
          try {
            storage.setItem(prefix + '_' + key, value);
            return;
          } catch (error) {
            // Fall back to memory in restricted browsing contexts.
          }
        }
        memory[key] = value;
      },
      remove: function (key) {
        if (storage) {
          try {
            storage.removeItem(prefix + '_' + key);
          } catch (error) {
            // Fall back to memory cleanup below.
          }
        }
        delete memory[key];
      }
    };
  }

  function normalizePath(path) {
    var value = String(path || '').trim().toLowerCase();

    if (!value) {
      return '';
    }

    try {
      if (/^https?:\/\//i.test(value)) {
        value = new URL(value).pathname;
      }
    } catch (error) {
      // Keep the original value when URL parsing is unavailable.
    }

    value = value.split('?')[0].split('#')[0].replace(/\/{2,}/g, '/');
    if (value.charAt(0) !== '/') {
      value = '/' + value;
    }

    if (value.indexOf('/page/') === 0) {
      value = '/pages/' + value.slice(6);
    }

    if (value.indexOf('/pages/') === 0) {
      value = '/pages/' + value.slice(7);
    }

    value = value.replace(/\/{2,}/g, '/').replace(/\/+$/, '');
    return value || '/';
  }

  function getExcludedPaths(rawPaths) {
    return String(rawPaths || '')
      .split(/\r?\n/)
      .map(normalizePath)
      .filter(Boolean);
  }

  function isPathExcluded(root) {
    var currentPath = normalizePath(root.getAttribute('data-page-path') || window.location.pathname);
    var excludedPaths = getExcludedPaths(root.getAttribute('data-excluded-paths'));

    return excludedPaths.some(function (excludedPath) {
      return currentPath === excludedPath;
    });
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(String(value || '').trim());
  }

  function getSubscriptionTag(source, collectionHandle) {
    switch (source) {
      case 'auto':
        return '订阅_自动弹窗';
      case 'reminder':
        return '订阅_右侧固标';
      case 'collection':
        var handle = String(collectionHandle || '').trim();
        return handle ? '订阅_' + handle + '_collection' : '';
      case 'pdp':
        return '订阅_PDP';
      case 'navigation':
        return '订阅_导航';
      default:
        return '';
    }
  }

  function getAnalyticsSource(source) {
    switch (source) {
      case 'auto':
        return 'auto_5s';
      case 'reminder':
        return 'floating';
      case 'navigation':
        return 'navbar';
      case 'collection':
        return 'collection';
      case 'pdp':
        return 'product';
      default:
        return '';
    }
  }

  function formatTravelPreferences(values) {
    return values.map(function (value) {
      return value.toLowerCase().replace(/[\/\\|+\-&‐‑‒–—―／＼｜＋＆－]+/g, '_');
    }).join('|');
  }

  function setAriaDisabled(button, disabled) {
    if (!button) {
      return;
    }

    button.disabled = disabled;
    button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  }

  function getPopupRoots() {
    return Array.prototype.slice.call(document.querySelectorAll(rootSelector));
  }

  function getPrimaryRoot() {
    var roots = getPopupRoots();
    var staticRoot = null;

    roots.some(function (root) {
      if (root.getAttribute('data-section-id') === 'subscription-popup') {
        staticRoot = root;
        return true;
      }
      return false;
    });

    return staticRoot || roots[0] || null;
  }

  function syncBodyLock() {
    if (!document.body) {
      return;
    }

    var body = document.body;
    var hasOpenModal = getPopupRoots().some(function (root) {
      var modal = root.querySelector('[data-popup-modal]');
      return !root.hidden && modal && !modal.hidden;
    });

    if (hasOpenModal) {
      if (!body.classList.contains('bv-subscription-popup-lock')) {
        var scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
        var currentPaddingRight = window.getComputedStyle(body).paddingRight;
        var basePaddingRight = parseFloat(currentPaddingRight) || 0;
        bodyLockState = {
          value: body.style.getPropertyValue('padding-right'),
          priority: body.style.getPropertyPriority('padding-right'),
          appliedValue: null,
          appliedPriority: null
        };
        if (scrollbarWidth > 0) {
          body.style.setProperty('padding-right', (basePaddingRight + scrollbarWidth) + 'px', 'important');
          bodyLockState.appliedValue = body.style.getPropertyValue('padding-right');
          bodyLockState.appliedPriority = body.style.getPropertyPriority('padding-right');
        }
        body.classList.add('bv-subscription-popup-lock');
      }
      return;
    }

    body.classList.remove('bv-subscription-popup-lock');
    if (bodyLockState) {
      // Restore only our own compensation. Another modal/app may have changed
      // the inline padding while the subscription dialog was open.
      if (bodyLockState.appliedValue !== null &&
          body.style.getPropertyValue('padding-right') === bodyLockState.appliedValue &&
          body.style.getPropertyPriority('padding-right') === bodyLockState.appliedPriority) {
        if (bodyLockState.value) {
          body.style.setProperty('padding-right', bodyLockState.value, bodyLockState.priority);
        } else {
          body.style.removeProperty('padding-right');
        }
      }
      bodyLockState = null;
    }
  }

  function deactivateDuplicateRoot(root) {
    if (!root) {
      return;
    }

    if (typeof root.__bvSubscriptionPopupDestroy === 'function') {
      root.__bvSubscriptionPopupDestroy();
    }

    var modal = root.querySelector('[data-popup-modal]');
    var reminderButton = root.querySelector('[data-popup-reminder]');

    if (modal) {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
    }
    if (reminderButton) {
      reminderButton.hidden = true;
      reminderButton.setAttribute('aria-hidden', 'true');
    }

    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    root.setAttribute('data-bv-subscription-popup-duplicate', 'true');
    syncBodyLock();
  }

  function init(root) {
    if (!root) {
      return;
    }

    if (root !== getPrimaryRoot()) {
      deactivateDuplicateRoot(root);
      return;
    }

    if (root.getAttribute(initializedAttribute) === 'true') {
      return;
    }

    root.hidden = false;
    root.removeAttribute('aria-hidden');
    root.removeAttribute('data-bv-subscription-popup-duplicate');

    var modal = root.querySelector('[data-popup-modal]');
    var dialog = root.querySelector('[data-popup-dialog]');
    var firstPanel = root.querySelector('[data-step-panel="first"]');
    var secondPanel = root.querySelector('[data-step-panel="second"]');
    var closeButton = root.querySelector('[data-popup-close]');
    var reminderButton = root.querySelector('[data-popup-reminder]');
    var reminderOpen = root.querySelector('[data-popup-reminder-open]');
    var reminderDismiss = root.querySelector('[data-popup-reminder-dismiss]');
    var firstForm = root.querySelector('form');
    var emailInput = root.querySelector('[data-email-input]');
    var consentInput = root.querySelector('[data-consent-input]');
    var firstSubmit = root.querySelector('[data-first-submit]');
    var emailError = root.querySelector('[data-email-error]');
    var preferencesError = root.querySelector('[data-preferences-error]');
    var preferenceInputs = Array.prototype.slice.call(root.querySelectorAll('[data-preference-option]'));
    var preferenceSubmit = root.querySelector('[data-preferences-submit]');
    var skipButton = root.querySelector('[data-skip-preferences]');
    var firstTitle = firstPanel ? firstPanel.querySelector('.bv-subscription-popup__title') : null;
    var secondTitle = secondPanel ? secondPanel.querySelector('.bv-subscription-popup__title') : null;
    var delay = parseInt(root.getAttribute('data-delay'), 10) || 5000;
    var isDesignMode = root.getAttribute('data-design-mode') === 'true';
    var isSearchPage = root.getAttribute('data-page-type') === 'search';
    var popupEligible = root.getAttribute('data-popup-eligible') !== 'false';
    var customerId = root.getAttribute('data-customer-id') || '';
    var customerStorageSuffix = customerId ? '_customer_' + customerId : '';
    var store = createSessionStore('bv_subscription_popup_v2' + customerStorageSuffix);
    var subscribedKey = 'bv_subscription_popup_subscribed_v1';
    var subscribedInMemory = root.getAttribute('data-customer-subscribed') === 'true'
      || document.documentElement.classList.contains('bv-subscription-popup-subscribed');
    var autoTimer = null;
    var autoReadyHandler = null;
    var warmupTimer = null;
    var warmupIdle = null;
    var warmupHeroCleanup = null;
    var focusTimer = null;
    var autoImageCleanup = null;
    var destroyed = false;
    var displayRequestId = 0;
    var reminderRequestId = 0;
    var firstStepLoading = false;
    var firstStepSource = '';
    var lastFocusedElement = null;
    var cleanups = [];
    var emailErrorKind = '';
    var firstPending = false;
    var secondPending = false;
    var firstRequestId = 0;
    var secondRequestId = 0;
    var firstController = null;
    var secondController = null;
    var submittedEmail = '';
    var activeSubscriptionTag = '';
    var analyticsSource = '';
    var submittedSubscriptionTag = '';
    var invalidEmailText = root.getAttribute('data-email-error-invalid') || 'The email address must contain @ and a valid domain.';
    var duplicateEmailText = root.getAttribute('data-email-error-duplicate') || "You're already subscribed.";
    var requestErrorText = root.getAttribute('data-email-error-request') || 'Unable to subscribe right now. Please try again.';

    if (!modal || !dialog || !firstPanel || !secondPanel || !closeButton || !reminderButton || !reminderOpen || !reminderDismiss) {
      return;
    }

    root.setAttribute(initializedAttribute, 'true');

    function listen(target, eventName, handler, options) {
      if (!target) {
        return;
      }

      target.addEventListener(eventName, handler, options);
      cleanups.push(function () {
        target.removeEventListener(eventName, handler, options);
      });
    }

    function trackGa4(eventName, parameters) {
      if (isDesignMode || typeof window.gtag !== 'function') return;
      queuePopupAnalytics(eventName, parameters);
    }

    function apiUrl(path) {
      var configuredBase = String(root.getAttribute('data-api-base-url') || '').trim();
      var origin = new URL(configuredBase || window.location.origin, window.location.origin);
      var url = new URL(path, origin.origin);
      if (url.protocol !== 'https:' && url.origin !== window.location.origin) {
        throw new Error('Subscription API requires an HTTPS origin.');
      }
      return url;
    }

    async function readApiResponse(response) {
      if (!response.ok) {
        throw new Error('Subscription API HTTP ' + response.status);
      }
      var result = await response.json();
      if (!result || result.code !== 200) {
        throw new Error('Subscription API code ' + (result && result.code));
      }
      return result.data;
    }

    function setButtonLoading(button, loading) {
      if (!button) {
        return;
      }
      button.classList.toggle('is-loading', loading);
      button.setAttribute('aria-busy', loading ? 'true' : 'false');
      var loadingLabel = button.querySelector('[data-loading-label]');
      if (loadingLabel) {
        loadingLabel.hidden = !loading;
      }
      setAriaDisabled(button, loading);
    }

    function showEmailError(kind, message) {
      emailErrorKind = kind;
      if (emailError) {
        emailError.textContent = message;
        emailError.hidden = false;
      }
      emailInput.setAttribute('aria-invalid', 'true');
    }

    function clearEmailError() {
      emailErrorKind = '';
      if (emailError) {
        emailError.hidden = true;
      }
      emailInput.setAttribute('aria-invalid', 'false');
    }

    function cancelFirstRequest() {
      if (!firstPending) {
        return;
      }
      firstRequestId += 1;
      if (firstController) {
        firstController.abort();
      }
      firstController = null;
      firstPending = false;
      setButtonLoading(firstSubmit, false);
      updateFirstValidation(false);
    }

    function cancelSecondRequest() {
      if (!secondPending) {
        return;
      }
      secondRequestId += 1;
      if (secondController) {
        secondController.abort();
      }
      secondController = null;
      secondPending = false;
      setButtonLoading(preferenceSubmit, false);
      updatePreferenceValidation();
    }

    function isSubscribed() {
      if (subscribedInMemory) return true;
      try {
        subscribedInMemory = window.localStorage.getItem(subscribedKey) === '1';
      } catch (error) {
        // Keep a known subscription even when persistent storage is unavailable.
      }
      return subscribedInMemory;
    }

    function rememberSubscription() {
      subscribedInMemory = true;
      store.set('flow_started', '1');
      try {
        window.localStorage.setItem(subscribedKey, '1');
      } catch (error) {
        // The current-session flag still works when persistent storage is blocked.
      }
      document.dispatchEvent(new CustomEvent('bv:subscription:success'));
    }

    function suppressSubscribedPrompts() {
      subscribedInMemory = true;
      cancelAutoOpen();
      cancelPopupWarmup();
      // A subscription update must not cancel an explicit navigation click.
      if (!firstStepLoading || firstStepSource !== 'navigation') {
        displayRequestId += 1;
        firstStepLoading = false;
        root.removeAttribute('aria-busy');
      }
      store.remove('reminder');
      hideReminder();
    }

    function isFlowStarted() {
      return isSubscribed() || store.get('flow_started') === '1' || store.get('flow_completed') === '1';
    }

    function canOpenFirstStep(source) {
      if (destroyed || !document.contains(root)) return false;
      // Explicit navigation clicks can reopen the offer regardless of subscription state.
      return source === 'navigation' || (popupEligible && !isFlowStarted() && !isPathExcluded(root));
    }

    function setActiveStep(step) {
      var isSecondStep = step === 'second';
      firstPanel.hidden = isSecondStep;
      secondPanel.hidden = !isSecondStep;
      modal.classList.toggle('is-step-two', isSecondStep);

      if (firstTitle) {
        firstTitle.setAttribute('aria-hidden', isSecondStep ? 'true' : 'false');
      }
      if (secondTitle) {
        secondTitle.setAttribute('aria-hidden', isSecondStep ? 'false' : 'true');
      }

      dialog.setAttribute('aria-labelledby', isSecondStep && secondTitle ? secondTitle.id : firstTitle ? firstTitle.id : '');
      modal.setAttribute('data-active-step', step);
    }

    function setBodyLock() {
      syncBodyLock();
    }

    function cancelFocus() {
      if (focusTimer !== null) {
        window.clearTimeout(focusTimer);
        focusTimer = null;
      }
    }

    function cancelAutoOpen() {
      if (autoReadyHandler) {
        document.removeEventListener('DOMContentLoaded', autoReadyHandler);
        autoReadyHandler = null;
      }
      if (autoTimer !== null) {
        window.clearTimeout(autoTimer);
        autoTimer = null;
      }
      if (autoImageCleanup) autoImageCleanup();
    }

    function cancelPopupWarmup() {
      if (warmupHeroCleanup) warmupHeroCleanup();
      if (warmupTimer !== null) {
        window.clearTimeout(warmupTimer);
        warmupTimer = null;
      }
      if (warmupIdle !== null) {
        if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(warmupIdle);
        warmupIdle = null;
      }

    }

    function prepareCriticalHeroCanvas(panel) {
      if (destroyed || isSearchPage || !panel) return;

      var image = panel.querySelector('[data-popup-critical-image]');
      var hero = image && image.closest('.bv-subscription-popup__hero');
      if (!image || !hero) return;

      var canvas = hero.querySelector('[data-popup-hero-canvas]');
      if (!canvas) {
        canvas = document.createElement('canvas');
        canvas.setAttribute('data-popup-hero-canvas', 'true');
        canvas.setAttribute('aria-hidden', 'true');
        canvas.style.display = 'none';
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.objectFit = 'cover';
        canvas.style.objectPosition = '50% 50%';
        hero.insertBefore(canvas, hero.firstChild);
      }

      var picture = hero.querySelector('picture');
      var revealPicture = function () {
        // Remove the failed canvas from the hero's flow so the picture is visible.
        canvas.style.display = 'none';
        if (picture) picture.style.visibility = 'visible';
      };
      var imageSource = image.currentSrc || image.src || '';
      if (canvas.getAttribute('data-popup-hero-ready') === 'true' && image.complete &&
          canvas.getAttribute('data-popup-hero-source') === imageSource &&
          canvas.width === image.naturalWidth && canvas.height === image.naturalHeight) {
        canvas.style.display = 'block';
        if (picture) picture.style.visibility = 'hidden';
        return;
      }
      // Keep the native image visible while a new source is still loading.
      revealPicture();
      canvas.removeAttribute('data-popup-hero-ready');
      if (canvas.getAttribute('data-popup-hero-pending') === 'true') return;

      var draw = function () {
        if (!image.naturalWidth || !image.naturalHeight) {
          revealPicture();
          return;
        }
        try {
          var context = canvas.getContext('2d');
          if (!context) {
            revealPicture();
            return;
          }
          canvas.width = image.naturalWidth;
          canvas.height = image.naturalHeight;
          context.clearRect(0, 0, canvas.width, canvas.height);
          context.drawImage(image, 0, 0, canvas.width, canvas.height);
          canvas.style.display = 'block';
          if (picture) picture.style.visibility = 'hidden';
          canvas.setAttribute('data-popup-hero-source', image.currentSrc || image.src || '');
          canvas.setAttribute('data-popup-hero-ready', 'true');
        } catch (error) {
          revealPicture();
        }
      };

      if (image.complete) {
        draw();
      } else {
        var cleanupImage = function () {
          image.removeEventListener('load', onImageLoad);
          image.removeEventListener('error', onImageError);
          canvas.removeAttribute('data-popup-hero-pending');
        };
        var onImageLoad = function () {
          cleanupImage();
          if (!destroyed) draw();
        };
        var onImageError = function () {
          cleanupImage();
          if (!destroyed) revealPicture();
        };
        canvas.setAttribute('data-popup-hero-pending', 'true');
        image.addEventListener('load', onImageLoad, { once: true });
        image.addEventListener('error', onImageError, { once: true });
        cleanups.push(cleanupImage);
      }
    }

    function openModal(step) {
      if (destroyed || !document.contains(root)) return;
      cancelFocus();
      loadPopupMedia(step === 'second' ? secondPanel : firstPanel);
      loadPopupMedia(root.querySelector('.bv-subscription-popup__close-icon--' + step));
      var wasHidden = modal.hidden;
      var previousStep = modal.getAttribute('data-active-step');
      if (wasHidden) {
        lastFocusedElement = document.activeElement;
      }

      setActiveStep(step);
      if (step === 'first') prepareCriticalHeroCanvas(firstPanel);
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      root.classList.add('is-open');
      setBodyLock(true);

      if (analyticsSource && step === 'first' && wasHidden) {
        trackGa4('subscribe_open', { source: analyticsSource });
      } else if (analyticsSource && step === 'second' && (wasHidden || previousStep !== 'second')) {
        trackGa4('travel_preference_open', { source: analyticsSource });
      }

      focusTimer = window.setTimeout(function () {
        focusTimer = null;
        if (!destroyed && !modal.hidden && document.contains(root)) {
          dialog.focus({ preventScroll: true });
        }
      }, 0);
    }

    function closeModal(restoreFocus) {
      cancelFocus();
      displayRequestId += 1;
      firstStepLoading = false;
      root.removeAttribute('aria-busy');
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      root.classList.remove('is-open');
      setBodyLock(false);

      if (restoreFocus !== false && lastFocusedElement && typeof lastFocusedElement.focus === 'function' && document.contains(lastFocusedElement)) {
        try {
          lastFocusedElement.focus({ preventScroll: true });
        } catch (error) {
          // Ignore focus restoration failures from removed theme-editor nodes.
        }
      }

      lastFocusedElement = null;
    }

    function hideReminder() {
      reminderRequestId += 1;
      reminderButton.hidden = true;
      reminderButton.setAttribute('aria-hidden', 'true');
    }

    function showReminder(markAsAutoShown, persistReminder, restoreFocus) {
      if (!canOpenFirstStep() || store.get('reminder_dismissed') === '1') {
        store.remove('reminder');
        hideReminder();
        return;
      }

      var requestId = ++reminderRequestId;
      loadPopupStyles(root).then(function () {
        if (destroyed || requestId !== reminderRequestId || firstStepLoading || !modal.hidden || !document.contains(root)) return;
        if (!canOpenFirstStep() || store.get('reminder_dismissed') === '1') return;
        loadPopupMedia(reminderButton);
        reminderButton.hidden = false;
        reminderButton.setAttribute('aria-hidden', 'false');
        // A search offer entry is not a dismissed or viewed modal.
        // Keep the automatic-popup state on other pages unchanged.
        if (persistReminder !== false) store.set('reminder', '1');
        if (markAsAutoShown) store.set('auto_shown', '1');
        // The offer must be visible before it can receive restored keyboard focus.
        // Leave focus alone if the visitor has already moved to another control.
        if (restoreFocus && (document.activeElement === document.body || modal.contains(document.activeElement))) {
          reminderOpen.focus({ preventScroll: true });
        }
      }).catch(function (error) {
        if (!destroyed && requestId === reminderRequestId) console.warn('Subscription reminder resources failed to load.', error);
      });
    }

    function warmPopupResources() {
      if (!canOpenFirstStep()) {
        return Promise.resolve();
      }

      // Prepare both resources together while the modal is
      // hidden; unrelated third-party load events must not postpone preparation.
      var styles = loadPopupStyles(root);
      loadPopupMedia(firstPanel, {
        loading: 'eager',
        fetchPriority: 'low',
        // Automatic-popup pages keep their hidden image below visible content.
        // Search requests this image only when the visitor opens the offer.
        criticalFetchPriority: root.getAttribute('data-popup-warmup-priority') || 'low'
      });
      // Draw while the modal is still hidden so opening it does not spend time on canvas work.
      prepareCriticalHeroCanvas(firstPanel);
      loadPopupMedia(root.querySelector('.bv-subscription-popup__close-icon--first'), { loading: 'eager', fetchPriority: 'low' });
      // Keep the collection hero ahead of the hidden popup. Once it finishes,
      // promote only the popup hero so it can be ready before automatic opening.
      if (root.getAttribute('data-early-init') === 'true' && root.getAttribute('data-popup-warmup-priority') === 'low') {
        var collectionHero = document.querySelector('main .collection-hero__image');
        var popupHero = firstPanel.querySelector('[data-popup-critical-image]');
        if (popupHero) {
          if (warmupHeroCleanup) warmupHeroCleanup();
          var promotePopupHero = function () {
            if (warmupHeroCleanup) warmupHeroCleanup();
            if (!canOpenFirstStep()) return;
            popupHero.setAttribute('fetchpriority', 'high');
          };
          var watchCollectionHero = function () {
            document.removeEventListener('DOMContentLoaded', watchCollectionHero);
            collectionHero = document.querySelector('main .collection-hero__image');
            if (!collectionHero) {
              promotePopupHero();
              return;
            }
            collectionHero.addEventListener('load', promotePopupHero);
            collectionHero.addEventListener('error', promotePopupHero);
            if (collectionHero.complete) promotePopupHero();
          };
          warmupHeroCleanup = function () {
            document.removeEventListener('DOMContentLoaded', watchCollectionHero);
            if (collectionHero) {
              collectionHero.removeEventListener('load', promotePopupHero);
              collectionHero.removeEventListener('error', promotePopupHero);
            }
            warmupHeroCleanup = null;
          };
          // This async script follows the popup but can precede main in the parser.
          // Keep low priority until the visible hero can actually be inspected.
          if (!collectionHero && document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', watchCollectionHero, { once: true });
          } else {
            watchCollectionHero();
          }
        }
      }
      return styles.catch(function (error) {
        if (!destroyed) console.warn('Subscription popup warmup failed.', error);
      });
    }

    function schedulePopupWarmup() {
      if (!canOpenFirstStep() || isSearchPage) return;
      // Collection initializes from the completed popup markup while the
      // rest of the document parses. Prepare media before busy app
      // initialization, while retaining the full five-second automatic delay.
      if (root.getAttribute('data-early-init') === 'true') {
        warmPopupResources();
        return;
      }
      // Other pages initialize after DOMContentLoaded. Leave the first render a head start,
      // then use idle time rather than waiting for every ad/analytics resource.
      warmupTimer = window.setTimeout(function () {
        warmupTimer = null;
        var run = function () {
          warmupIdle = null;
          warmPopupResources();
        };
        if (typeof window.requestIdleCallback === 'function') {
          warmupIdle = window.requestIdleCallback(run, { timeout: 900 });
        } else {
          run();
        }
      }, Math.max(0, delay - 2200));
    }

    function waitForFirstScreenImage(onReady) {
      var candidate = null;
      var largestArea = 0;
      // Read geometry once at the scheduled opening time. Offscreen/lazy media
      // and small icons must not delay the first-step experience.
      Array.prototype.forEach.call(document.querySelectorAll('main img'), function (image) {
        var rect = image.getBoundingClientRect();
        var width = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
        var height = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
        if (width >= 120 && height >= 80 && width * height > largestArea) {
          largestArea = width * height;
          candidate = image;
        }
      });
      if (!candidate || candidate.complete) {
        onReady();
        return;
      }
      var finish = function () {
        if (!autoImageCleanup) return;
        autoImageCleanup();
        onReady();
      };
      autoImageCleanup = function () {
        candidate.removeEventListener('load', finish);
        candidate.removeEventListener('error', finish);
        if (autoTimer !== null) window.clearTimeout(autoTimer);
        autoTimer = null;
        autoImageCleanup = null;
      };
      candidate.addEventListener('load', finish);
      candidate.addEventListener('error', finish);
      autoTimer = window.setTimeout(finish, 1200);
      if (candidate.complete) finish();
    }

    function showFirstStep(markAsAutoShown, source, collectionHandle, options) {
      if (!canOpenFirstStep(source)) {
        return;
      }

      var settings = options || {};
      var requestId = ++displayRequestId;
      firstStepLoading = true;
      firstStepSource = source || '';
      root.setAttribute('aria-busy', 'true');
      var styles = loadPopupStyles(root);
      var criticalImage = firstPanel.querySelector('[data-popup-critical-image]');
      if (criticalImage) criticalImage.setAttribute('fetchpriority', 'high');
      loadPopupMedia(firstPanel);
      loadPopupMedia(root.querySelector('.bv-subscription-popup__close-icon--first'));
      // Search opens the styled, dimensioned form immediately on explicit intent;
      // the optional hero must not add a cold-image wait to the offer click.
      var imageReady = isSearchPage ? Promise.resolve() : waitForPopupImage(criticalImage, settings.imageWait || 1200);
      Promise.all([styles, imageReady]).then(function () {
        if (destroyed || requestId !== displayRequestId) return;
        firstStepLoading = false;
        root.removeAttribute('aria-busy');
        if (!canOpenFirstStep(source)) return;
        if (markAsAutoShown) store.set('auto_shown', '1');
        store.remove('reminder');
        var restoreReminderFocus = isSearchPage && document.activeElement === reminderOpen;
        hideReminder();
        activeSubscriptionTag = getSubscriptionTag(source, collectionHandle);
        analyticsSource = getAnalyticsSource(source);
        openModal('first');
        if (restoreReminderFocus) lastFocusedElement = reminderOpen;
      }).catch(function (error) {
        if (destroyed || requestId !== displayRequestId) return;
        firstStepLoading = false;
        root.removeAttribute('aria-busy');
        // Keep the reminder and auto_shown state unchanged; never reveal unstyled UI.
        console.warn('Subscription popup resources failed to load.', error);
      });
    }

    function closeFirstStep() {
      if (modal.getAttribute('data-active-step') !== 'first') {
        return;
      }

      cancelFirstRequest();
      store.set('dismissed', '1');
      var restoreReminderFocus = isSearchPage && lastFocusedElement === reminderOpen;
      closeModal();
      showReminder(false, undefined, restoreReminderFocus);
    }

    function finishFlow() {
      cancelFirstRequest();
      cancelSecondRequest();
      store.set('flow_started', '1');
      store.set('flow_completed', '1');
      store.remove('reminder');
      hideReminder();
      closeModal();
    }

    function updateFirstValidation(showError) {
      if (!emailInput || !consentInput || !firstSubmit) {
        return false;
      }

      var emailValue = emailInput.value.trim();
      var hasEmail = emailValue.length > 0;
      var validEmail = hasEmail && isValidEmail(emailValue);
      var validConsent = consentInput.checked;
      var canSubmit = validConsent && (!hasEmail || validEmail);

      setAriaDisabled(firstSubmit, firstPending || !canSubmit);
      if (!validEmail && (showError || emailErrorKind === 'validation')) {
        showEmailError('validation', invalidEmailText);
      } else if (emailErrorKind === 'validation') {
        clearEmailError();
      } else if (!emailErrorKind) {
        emailInput.setAttribute('aria-invalid', hasEmail && !validEmail ? 'true' : 'false');
      }

      return validEmail && validConsent;
    }

    function updatePreferenceValidation() {
      if (!preferenceSubmit) {
        return;
      }

      var selectedCount = 0;
      preferenceInputs.forEach(function (input) {
        var option = input.closest('.bv-subscription-popup__option');
        if (input.checked) {
          selectedCount += 1;
        }
        if (option) {
          option.classList.toggle('is-selected', input.checked);
        }
      });

      setAriaDisabled(preferenceSubmit, secondPending || selectedCount === 0);
    }

    async function submitSubscription(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
      }
      if (firstPending || modal.hidden || modal.getAttribute('data-active-step') !== 'first') {
        return;
      }
      if (!updateFirstValidation(true)) {
        if (!isValidEmail(emailInput.value.trim())) {
          emailInput.focus();
        }
        return;
      }

      clearEmailError();
      var email = emailInput.value.trim();
      var subscriptionTag = activeSubscriptionTag;
      var submitSource = analyticsSource;
      var requestId = ++firstRequestId;
      firstPending = true;
      firstController = typeof AbortController === 'function' ? new AbortController() : null;
      setButtonLoading(firstSubmit, true);
      var signal = firstController ? firstController.signal : undefined;

      try {
        var statusUrl = apiUrl('/api/customers/get-subscribe-status');
        statusUrl.searchParams.set('email', email);
        statusUrl.searchParams.set('shop', 'bougerv_us');
        var statusRequest = window.fetch(statusUrl.toString(), {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded' },
          credentials: 'same-origin',
          signal: signal
        });
        if (submitSource) {
          trackGa4('subscribe_submit', { source: submitSource });
        }
        var statusResponse = await statusRequest;
        var alreadySubscribed = await readApiResponse(statusResponse);
        if (requestId !== firstRequestId) {
          return;
        }
        if (typeof alreadySubscribed !== 'boolean') {
          throw new Error('Subscription API returned a non-boolean status.');
        }
        if (alreadySubscribed) {
          rememberSubscription();
          trackGa4('subscribe_result', { source_result: 'already_subscribed' });
          showEmailError('duplicate', duplicateEmailText);
          return;
        }

        var createResponse = await window.fetch(apiUrl('/api/customers/action').toString(), {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ email: email, shop: 'bougerv_us', subscribe: true, tags: subscriptionTag ? [subscriptionTag] : [] }),
          signal: signal
        });
        await readApiResponse(createResponse);
        if (requestId !== firstRequestId) {
          return;
        }

        submittedEmail = email;
        submittedSubscriptionTag = subscriptionTag;
        analyticsSource = submitSource;
        trackGa4('subscribe_result', { source_result: 'success' });
        rememberSubscription();
        store.remove('reminder');
        hideReminder();
        openModal('second');
      } catch (error) {
        if (requestId !== firstRequestId || error.name === 'AbortError') {
          return;
        }
        console.warn('Subscription request failed.', error);
        trackGa4('subscribe_result', { source_result: 'failed' });
        showEmailError('request', requestErrorText);
      } finally {
        if (requestId === firstRequestId) {
          firstPending = false;
          firstController = null;
          setButtonLoading(firstSubmit, false);
          updateFirstValidation(false);
        }
      }
    }

    function closeActiveStep() {
      if (modal.getAttribute('data-active-step') === 'second') {
        finishFlow();
      } else {
        closeFirstStep();
      }
    }

    async function submitPreferences() {
      if (secondPending || modal.hidden || modal.getAttribute('data-active-step') !== 'second') {
        return;
      }
      var selectedValues = preferenceInputs
        .filter(function (input) { return input.checked; })
        .map(function (input) { return String(input.value || '').trim(); })
        .filter(Boolean);
      if (!selectedValues.length) {
        return;
      }
      if (preferencesError) {
        preferencesError.hidden = true;
      }
      if (!submittedEmail) {
        if (preferencesError) {
          preferencesError.hidden = false;
        }
        return;
      }

      var requestId = ++secondRequestId;
      secondPending = true;
      secondController = typeof AbortController === 'function' ? new AbortController() : null;
      setButtonLoading(preferenceSubmit, true);
      try {
        var response = await window.fetch(apiUrl('/api/customers/action').toString(), {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            email: submittedEmail,
            shop: 'bougerv_us',
            subscribe: true,
            tags: (submittedSubscriptionTag ? [submittedSubscriptionTag] : []).concat(
              selectedValues.map(function (value) { return '出行偏好_' + value; })
            )
          }),
          signal: secondController ? secondController.signal : undefined
        });
        await readApiResponse(response);
        if (requestId !== secondRequestId) {
          return;
        }
        trackGa4('travel_preference_save', { travel_preference: formatTravelPreferences(selectedValues) });
        finishFlow();
      } catch (error) {
        if (requestId !== secondRequestId || error.name === 'AbortError') {
          return;
        }
        console.warn('Preferences request failed.', error);
        if (preferencesError) {
          preferencesError.hidden = false;
        }
      } finally {
        if (requestId === secondRequestId) {
          secondPending = false;
          secondController = null;
          setButtonLoading(preferenceSubmit, false);
          updatePreferenceValidation();
        }
      }
    }

    function handleKeydown(event) {
      if (event.key !== 'Escape' || modal.hidden) {
        return;
      }

      if (modal.getAttribute('data-active-step') === 'second') {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
        return;
      }

      closeActiveStep();
    }

    setActiveStep('first');
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    hideReminder();
    updateFirstValidation(false);
    updatePreferenceValidation();

    listen(emailInput, 'input', function () {
      cancelFirstRequest();
      if (emailErrorKind !== 'validation') {
        clearEmailError();
      }
      updateFirstValidation(false);
    });
    listen(emailInput, 'blur', function (event) {
      // Keep the consent target still between pointerdown and pointerup.
      updateFirstValidation(event.relatedTarget !== consentInput);
    });
    listen(consentInput, 'change', function () {
      cancelFirstRequest();
      updateFirstValidation(false);
    });
    listen(firstSubmit, 'click', submitSubscription);
    listen(firstForm, 'submit', submitSubscription, true);

    preferenceInputs.forEach(function (input) {
      listen(input, 'change', function () {
        cancelSecondRequest();
        if (preferencesError) {
          preferencesError.hidden = true;
        }
        updatePreferenceValidation();
      });
    });

    listen(document, 'bv:subscription:success', suppressSubscribedPrompts);
    listen(window, 'storage', function (event) {
      if (event.key !== subscribedKey || event.newValue !== '1') {
        return;
      }

      suppressSubscribedPrompts();
    });

    listen(closeButton, 'click', function () {
      if (modal.hidden) {
        return;
      }
      if (modal.getAttribute('data-active-step') === 'second') {
        trackGa4('travel_preference_skip');
      } else if (analyticsSource) {
        trackGa4('subscribe_close', { source: analyticsSource });
      }
      closeActiveStep();
    });
    listen(reminderOpen, 'click', function () {
      if (!isFlowStarted()) {
        if (isSearchPage) {
          cancelAutoOpen();
          cancelPopupWarmup();
        }
        showFirstStep(false, 'reminder');
      }
    });
    listen(document, 'click', function (event) {
      var trigger = event.target && event.target.closest
        ? event.target.closest('[data-bv-subscription-popup-open]')
        : null;
      if (!trigger) {
        return;
      }

      event.preventDefault();
      root.__bvSubscriptionPopupOpen(trigger.getAttribute('data-bv-subscription-source'));
    });
    listen(reminderDismiss, 'click', function () {
      store.set('reminder_dismissed', '1');
      store.remove('reminder');
      hideReminder();
    });
    listen(skipButton, 'click', function () {
      if (modal.hidden || modal.getAttribute('data-active-step') !== 'second') {
        return;
      }
      trackGa4('travel_preference_skip');
      finishFlow();
    });
    listen(preferenceSubmit, 'click', submitPreferences);
    listen(document, 'keydown', handleKeydown, true);

    root.__bvSubscriptionPopupOpen = function (source, collectionHandle) {
      if (!canOpenFirstStep(source)) {
        return false;
      }
      cancelAutoOpen();
      cancelPopupWarmup();
      showFirstStep(false, source, collectionHandle);
      return true;
    };

    root.__bvSubscriptionPopupDestroy = function () {
      if (destroyed) return;
      destroyed = true;
      hideReminder();
      cancelFirstRequest();
      cancelSecondRequest();
      cancelAutoOpen();
      cancelPopupWarmup();
      cancelFocus();

      cleanups.forEach(function (cleanup) {
        cleanup();
      });
      cleanups = [];
      closeModal(false);
      root.removeAttribute(initializedAttribute);
      delete root.__bvSubscriptionPopupOpen;
      delete root.__bvSubscriptionPopupDestroy;
    };

    // Register navigation first so subscribers can still open the popup explicitly.
    if (isSubscribed()) {
      rememberSubscription();
      return;
    }

    // Keep automatic popups and reminders gated after registering the navigation handler.
    if (!popupEligible || isPathExcluded(root)) {
      hideReminder();
      return;
    }

    if (isDesignMode && store.get('dismissed') !== '1') {
      showFirstStep(false);
      return;
    }

    if (isFlowStarted()) {
      hideReminder();
      return;
    }

    if (store.get('dismissed') === '1' || store.get('reminder') === '1') {
      showReminder();
      return;
    }

    if (store.get('auto_shown') !== '1') {
      schedulePopupWarmup();
      var openAutomatically = function () {
        autoTimer = null;
        autoReadyHandler = null;
        if (!canOpenFirstStep() || store.get('dismissed') === '1') {
          return;
        }
        // Never show an automatic modal over a document that is still parsing.
        if (document.readyState === 'loading') {
          autoReadyHandler = openAutomatically;
          document.addEventListener('DOMContentLoaded', autoReadyHandler, { once: true });
          return;
        }

        if (isSearchPage) {
          showReminder(false, false);
          return;
        }

        waitForFirstScreenImage(function () {
          if (!canOpenFirstStep() || store.get('dismissed') === '1') return;
          // Preserve the full five-second modal, with only a bounded allowance
          // for the visible main image and the popup image, not the whole page.
          showFirstStep(true, 'auto', '', { imageWait: 400 });
        });
      };
      autoTimer = window.setTimeout(openAutomatically, delay);
    }
  }

  function initAll(scope) {
    var context = scope && typeof scope.querySelectorAll === 'function' ? scope : document;

    if (context.matches && context.matches(rootSelector)) {
      init(context);
    }

    Array.prototype.forEach.call(context.querySelectorAll(rootSelector), init);
  }

  function getEventRoot(event) {
    var target = event && event.target;
    var sectionId = event && event.detail && event.detail.sectionId;

    if (target) {
      if (target.matches && target.matches(rootSelector)) {
        return target;
      }
      if (typeof target.querySelector === 'function') {
        var nestedRoot = target.querySelector(rootSelector);
        if (nestedRoot) {
          return nestedRoot;
        }
      }
    }

    return sectionId
      ? document.querySelector(rootSelector + '[data-section-id="' + sectionId + '"]')
      : null;
  }

  window.BvSubscriptionPopup = {
    initAll: initAll,
    open: function (source, collectionHandle) {
      var root = getPrimaryRoot();
      if (!root) {
        return false;
      }
      init(root);
      return typeof root.__bvSubscriptionPopupOpen === 'function' && root.__bvSubscriptionPopupOpen(source, collectionHandle);
    }
  };

  // The async script is emitted after the complete static popup root.
  // If an integration injects it earlier, wait until the document has parsed.
  var earlyRootReady = document.querySelector(rootSelector + ' [data-popup-reminder-dismiss]');
  if (document.readyState === 'loading' && !earlyRootReady) {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    }, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = getEventRoot(event);
    if (root) {
      init(root);
    } else {
      initAll();
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = getEventRoot(event);
    if (root && typeof root.__bvSubscriptionPopupDestroy === 'function') {
      root.__bvSubscriptionPopupDestroy();
    }
  });
})();
